import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { Icon } from "@circle-react-shared/Icon";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { useModalData } from "@circle-react-shared/uikit/ModalV2";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { DeleteRecurringModal } from "@circle-react/components/Events/DeleteRecurringModal";
import { DuplicateEventModal } from "@circle-react/components/Events/ListViewItem/EventMenu/DuplicateEventModal";
import { useEventMenu } from "@circle-react/components/Events/ListViewItem/EventMenu/hooks/useEventMenu";
import { PinEventToTopAction } from "@circle-react/components/Events/ListViewItem/EventMenu/PinEventToTopAction";
import { ShareEvent } from "@circle-react/components/Events/NewEventsSpace/EventDetailPage/ShareEvent";
import {
  eventDeletionMessage,
  isPaidEvent,
} from "@circle-react/components/EventsV3/Modal/utils";
import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Post } from "@circle-react/types/Post";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { eventApi } from "@/react/api/eventApi";
import type { RecurringEditMode } from "@/react/components/Events/helpers/constants";
import { useConvertEventToBroadcast } from "@/react/components/EventsV3/Modal/hooks/useConvertEventToBroadcast";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { ConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/ConfirmationModal";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { isCommunityAdmin } from "@/react/helpers/communityMemberHelpers";

interface EventDropdownActionsUIKitComponentProps {
  onChange: (data?: Post) => void;
  onPostDestroy: (...args: unknown[]) => void;
}

export const EventDropdownActionsUIKitComponent = ({
  onChange,
  onPostDestroy,
}: EventDropdownActionsUIKitComponentProps) => {
  const {
    post: event,
    canUpdatePost,
    canDuplicatePost,
    canDestroyPost,
    canManagePost,
    refetchPostDetails,
  } = useCurrentPostHelpers();

  const editEventModal = useEditEventModal();
  const { success, error } = useToast();
  const { refetch } = useCurrentSpaceContext();
  const isSmScreen = useSmScreenMediaQuery();
  const [shouldShowDuplicateEventModal, toggleDuplicateEventModal] =
    useBoolean(false);
  const {
    onDeleteClickHandler,
    isConfirmationModalOpen,
    toggleConfirmationModal,
    isRecurringModalOpen,
    toggleRecurringModal,
  } = useEventMenu({ event });
  const { isViewOnlyMasquerading, currentCommunityMember } =
    usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const isEventShareableViaBroadcast = isAdmin && event.status !== "draft";

  const { onSendAnnouncement, isPending: isSendingAnnouncement } =
    useConvertEventToBroadcast();

  const { contentPortalElement } = useModalData();

  const deleteMutation = useMutation({
    mutationFn: (deleteMode: RecurringEditMode | "") =>
      eventApi.destroy({ event: event, params: { delete_mode: deleteMode } }),
    onSuccess: () => {
      onPostDestroy(event.id);
      success(eventDeletionMessage(event), {
        duration: "short",
        shouldUseProgress: false,
      });
      void refetch?.();
    },
    onError: () => error(t("event_dropdown_actions.delete_failed")),
  });

  const shouldDisplayDropdownActions =
    canUpdatePost || canDuplicatePost || canDestroyPost;

  if (!shouldDisplayDropdownActions) {
    return null;
  }

  if (isViewOnlyMasquerading) {
    return (
      <Icon
        type="menu-dots"
        aria-label={t("events.event_actions")}
        className="cursor-not-allowed"
      />
    );
  }

  return (
    <>
      <Dropdown
        direction="bottom-end"
        menuButtonEl="div"
        button={
          <IconButton
            name="20-menu-dots-horizontal"
            iconClassName="!text-default group-hover:!text-dark !w-5 !h-5"
            className="group"
            variant="secondary"
            iconSize={20}
            buttonSize={28}
            ariaLabel={t("events.event_actions")}
            dataTestId="action-more"
          />
        }
        className="z-10"
        transitionWrapperClassName="z-50"
        size="large"
        appendTo={contentPortalElement ?? document.body}
      >
        <BookmarkButton
          variant="dropdown"
          bookmarkType="event"
          record={event}
          onSuccess={refetchPostDetails}
        />
        {isSmScreen && (
          <ShareEvent
            onChange={() => onChange()}
            button={
              <Dropdown.ItemLink>
                {t("events.event_actions_dropdown.share_event")}
              </Dropdown.ItemLink>
            }
          />
        )}
        {canUpdatePost && (
          <Dropdown.ItemLink
            onClick={() => editEventModal.show({ event } as any)}
            forceClose
          >
            {t("edit_event")}
          </Dropdown.ItemLink>
        )}
        {canDuplicatePost && (
          <Dropdown.ItemLink forceClose onClick={toggleDuplicateEventModal}>
            {t("duplicate_event")}
          </Dropdown.ItemLink>
        )}
        {canDestroyPost && (
          <Dropdown.ItemLink onClick={onDeleteClickHandler} forceClose>
            <span className="text-v2-danger">{t("delete_event")}</span>
          </Dropdown.ItemLink>
        )}
        {isEventShareableViaBroadcast && (
          <>
            <Dropdown.Separator />
            <Dropdown.ItemLink
              data-testid="send-announcement-event-action"
              disabled={isSendingAnnouncement}
              onClick={() => onSendAnnouncement({ eventId: event.id })}
            >
              <div className="flex items-center justify-between gap-2">
                {t("share_via_broadcast")}
                <Badge label={t("new")} square variant="secondary" />
              </div>
            </Dropdown.ItemLink>
          </>
        )}
        {canManagePost && (
          <>
            <Dropdown.Separator />
            <PinEventToTopAction event={event} canEdit={canUpdatePost} />
          </>
        )}
      </Dropdown>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        title={t("event_delete_modal.confirm.title")}
        onClose={toggleConfirmationModal}
        onCancel={toggleConfirmationModal}
        onConfirm={() => deleteMutation.mutate("")}
        confirmVariant="danger"
        loading={deleteMutation.isPending}
        confirmText={t("delete_event")}
      >
        {t("event_delete_modal.confirm.description")}
      </ConfirmationModal>
      <DuplicateEventModal
        event={event}
        show={shouldShowDuplicateEventModal}
        onClose={toggleDuplicateEventModal}
      />
      {!isPaidEvent(event) && (
        <DeleteRecurringModal
          show={isRecurringModalOpen}
          isDeleting={deleteMutation.isPending}
          onConfirm={(frequency: RecurringEditMode) =>
            deleteMutation.mutate(frequency)
          }
          onCloseModal={toggleRecurringModal}
        />
      )}
    </>
  );
};
